import * as React from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { MessageKind } from 'uiComponents/messages'
import { parseSearch } from 'navigation'
import { LucideIcon } from 'utils/lucideIcon'
import { useUrlParam } from 'utils/useUrlParam'
import { useGetCapacityListQuery, useGetCapacityMemberCountsQuery } from 'capacity/reduxQuery'
import { capacityPaths } from 'capacity/paths'
import { CapacitySearch } from './components/capacitySearch'
import { CapacitiesTable } from './components/capacitiesTable'
import { VisitorsSection } from './components/visitorsSection'
import { buildListRows, matchesSearch } from './model/listModel'
import '../capacityPage/products/productsTable.scss'
import './capacityListPage.scss'
import { useTranslation } from 'translations'

export interface CapacityListPageProps {
    accountSlug: string
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    hideMessage: (id: string) => void
}

export function CapacityListPage({
    accountSlug,
    setActiveSection,
    replaceMessages,
    hideMessage,
}: CapacityListPageProps) {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const list = useGetCapacityListQuery(accountSlug)
    const counts = useGetCapacityMemberCountsQuery(accountSlug)
    const [search, setSearch] = useUrlParam('search')
    const searchInput = React.useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        setActiveSection('inventory', t('capacity.section.title'))
    }, [])

    React.useEffect(() => {
        const poolId = parseSearch(location.search).poolId
        if (poolId) {
            history.replace(capacityPaths.item(accountSlug, poolId))
        }
    }, [location.search])

    const rows = React.useMemo(() => buildListRows(list.data?.capacities ?? [], counts.data), [list.data, counts.data])
    const visibleRows = React.useMemo(() => rows.filter((row) => matchesSearch(row, search)), [rows, search])
    const loading = list.isLoading
    const empty = !loading && !list.isError && rows.length === 0

    const clearSearch = () => {
        setSearch(null)
        searchInput.current?.focus()
    }

    return (
        <div className="capacity-list-page">
            <VisitorsSection
                accountSlug={accountSlug}
                visitors={list.data?.visitors}
                failed={list.isError}
                counts={counts.data}
                countsFailed={counts.isError}
                replaceMessages={replaceMessages}
                hideMessage={hideMessage}
            />
            <section className="capacity-list-section" aria-labelledby="capacities-heading">
                <div className="capacity-list-heading">
                    <h2 id="capacities-heading" className="capacity-list-title">
                        {t('capacity.list.capacities')}
                    </h2>
                    <p className="capacity-list-meaning">{t('capacity.list.capacitiesMeaning')}</p>
                </div>
                <div className="capacity-list-toolbar">
                    <CapacitySearch value={search} onChange={setSearch} inputRef={searchInput} collapsed={empty} />
                    <Link to={capacityPaths.create(accountSlug)} className="capacity-list-create" id="newInventory">
                        <LucideIcon iconName="Plus" size={16} strokeWidth={2} />
                        {t('capacity.list.createNew')}
                    </Link>
                </div>
                <CapacitiesTable
                    accountSlug={accountSlug}
                    rows={visibleRows}
                    loading={loading}
                    error={list.isError}
                    onRetry={list.refetch}
                    countsFailed={counts.isError}
                    onRetryCounts={counts.refetch}
                    empty={empty}
                    query={search}
                    onClearSearch={clearSearch}
                />
            </section>
        </div>
    )
}

export default CapacityListPage
